import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { SwitchButton } from "./ui/switch.jsx";
import { showInfo, showError } from "../utils/toast";
import "../sass/pages/contratoClientePanel.scss";

const vazio = {
  cliente: {}, signatario: {}, comercial: {}, canais: { email: true, whatsapp: false },
  planos: [], planoSelecionado: null, atualizacaoAutomatica: true,
};

const moedaInput = (v) => {
  const n = Number(String(v ?? "0").replace(/\./g, "").replace(",", "."));
  return Number.isFinite(n) ? n.toFixed(2).replace(".", ",") : "0,00";
};

const numeroMoeda = (v) => {
  const s = String(v ?? "").trim().replace(/R\$/gi, "").replace(/\s/g, "");
  if (!s) return 0;
  const normalized = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const statusLabel = (status) => ({
  AGUARDANDO_ASSINATURA: "Aguardando assinatura",
  VISUALIZADO: "Visualizado",
  ASSINADO: "Assinado",
  CANCELADO: "Cancelado",
  SUBSTITUIDO: "Substituído",
  REVISAO_NECESSARIA: "Revisão necessária",
  ERRO_GERACAO: "Erro na geração",
  GERADO: "Gerado",
}[status] || status || "-");

function ModalPrepararContrato({ open, codigoCad, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [dados, setDados] = useState(vazio);
  const [atualizarCadastro, setAtualizarCadastro] = useState(false);

  const carregar = useCallback(async () => {
    if (!open || !codigoCad) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/cliente/${codigoCad}/contratos/preparar`);
      const plano = data.planoSelecionado || data.planos?.[0] || null;
      setDados({
        ...vazio,
        ...data,
        comercial: {
          ...data.comercial,
          idPlano: plano?.id || "",
          valorMensal: moedaInput(plano?.valorCobrado || 0),
          valorAdesao: moedaInput(data.comercial?.valorAdesao || 0),
          valorHoraTecnica: moedaInput(data.comercial?.valorHoraTecnica || 0),
          valorAtivacaoSat: moedaInput(data.comercial?.valorAtivacaoSat || 0),
          valorEstacoesAdicionais: moedaInput(data.comercial?.valorEstacoesAdicionais || 0),
        },
      });
      setAtualizarCadastro(false);
    } catch (e) {
      showError(e?.response?.data?.message || "Erro ao preparar contrato.");
      onClose?.();
    } finally { setLoading(false); }
  }, [open, codigoCad, onClose]);

  useEffect(() => { carregar(); }, [carregar]);

  const alterar = (grupo, campo, valor) => {
    setDados(prev => ({ ...prev, [grupo]: { ...(prev[grupo] || {}), [campo]: valor } }));
  };

  const selecionarPlano = (id) => {
    const plano = dados.planos.find(p => Number(p.id) === Number(id));
    setDados(prev => ({
      ...prev,
      planoSelecionado: plano || null,
      comercial: { ...prev.comercial, idPlano: id, valorMensal: moedaInput(plano?.valorCobrado || 0) },
    }));
  };

  const enviar = async () => {
    if (!dados.comercial?.idPlano) return showError("Selecione o plano contratual.");
    if (!dados.signatario?.nome || !dados.signatario?.cpf || !dados.signatario?.cargo || !dados.signatario?.email) {
      return showError("Preencha nome, CPF, cargo e e-mail do signatário.");
    }
    setEnviando(true);
    try {
      const payload = {
        cliente: dados.cliente,
        signatario: dados.signatario,
        comercial: {
          ...dados.comercial,
          idPlano: Number(dados.comercial.idPlano),
          valorMensal: numeroMoeda(dados.comercial.valorMensal),
          valorAdesao: numeroMoeda(dados.comercial.valorAdesao),
          valorHoraTecnica: numeroMoeda(dados.comercial.valorHoraTecnica),
          valorAtivacaoSat: numeroMoeda(dados.comercial.valorAtivacaoSat),
          valorEstacoesAdicionais: numeroMoeda(dados.comercial.valorEstacoesAdicionais),
        },
        canais: dados.canais,
        atualizacaoAutomatica: dados.atualizacaoAutomatica !== false,
        atualizarCadastro,
      };
      await api.post(`/cliente/${codigoCad}/contratos/gerar-enviar`, payload, { timeout: 120000 });
      showInfo("Contrato gerado e envio colocado na fila.");
      onSuccess?.();
      onClose?.();
    } catch (e) {
      showError(e?.response?.data?.message || e.message || "Erro ao gerar contrato.");
    } finally { setEnviando(false); }
  };

  if (!open) return null;
  return (
    <div className="contrato-modal-backdrop" onMouseDown={e => e.target === e.currentTarget && !enviando && onClose?.()}>
      <section className="contrato-modal" role="dialog" aria-modal="true">
        <header className="contrato-modal-header">
          <div><small>Cliente {codigoCad}</small><h2>Preparar contrato</h2></div>
          <button type="button" onClick={onClose} disabled={enviando}>×</button>
        </header>
        {loading ? <div className="contrato-loading">Carregando dados contratuais...</div> : (
          <div className="contrato-modal-body">
            <div className="contrato-form-section">
              <h3>Dados da licenciada</h3>
              <div className="contrato-grid contrato-grid-3">
                <div><Label>Razão Social</Label><Input value={dados.cliente?.razaoSocial || ""} onChange={e=>alterar("cliente","razaoSocial",e.target.value)} /></div>
                <div><Label>Nome Fantasia</Label><Input value={dados.cliente?.nomeFantasia || ""} onChange={e=>alterar("cliente","nomeFantasia",e.target.value)} /></div>
                <div><Label>CNPJ/CPF</Label><Input value={dados.cliente?.cpfCnpj || ""} onChange={e=>alterar("cliente","cpfCnpj",e.target.value)} /></div>
                <div><Label>IE/RG</Label><Input value={dados.cliente?.rgIe || ""} onChange={e=>alterar("cliente","rgIe",e.target.value)} /></div>
                <div><Label>E-mail</Label><Input value={dados.cliente?.email || ""} onChange={e=>alterar("cliente","email",e.target.value)} /></div>
                <div><Label>Telefone</Label><Input value={dados.cliente?.telefone || ""} onChange={e=>alterar("cliente","telefone",e.target.value)} /></div>
                <div className="span-2"><Label>Endereço</Label><Input value={dados.cliente?.endereco || ""} onChange={e=>alterar("cliente","endereco",e.target.value)} /></div>
                <div><Label>Número</Label><Input value={dados.cliente?.numero || ""} onChange={e=>alterar("cliente","numero",e.target.value)} /></div>
                <div><Label>Bairro</Label><Input value={dados.cliente?.bairro || ""} onChange={e=>alterar("cliente","bairro",e.target.value)} /></div>
                <div><Label>Cidade</Label><Input value={dados.cliente?.cidade || ""} onChange={e=>alterar("cliente","cidade",e.target.value)} /></div>
                <div><Label>UF</Label><Input value={dados.cliente?.uf || ""} maxLength={2} onChange={e=>alterar("cliente","uf",e.target.value.toUpperCase())} /></div>
              </div>
              <label className="contrato-check"><input type="checkbox" checked={atualizarCadastro} onChange={e=>setAtualizarCadastro(e.target.checked)} /> Atualizar também o cadastro principal com os dados acima</label>
            </div>

            <div className="contrato-form-section">
              <h3>Signatário da licenciada</h3>
              <div className="contrato-grid contrato-grid-3">
                <div><Label>Nome completo</Label><Input value={dados.signatario?.nome || ""} onChange={e=>alterar("signatario","nome",e.target.value)} /></div>
                <div><Label>CPF</Label><Input value={dados.signatario?.cpf || ""} onChange={e=>alterar("signatario","cpf",e.target.value)} /></div>
                <div><Label>Cargo</Label><Input value={dados.signatario?.cargo || ""} onChange={e=>alterar("signatario","cargo",e.target.value)} /></div>
                <div><Label>E-mail de assinatura</Label><Input value={dados.signatario?.email || ""} onChange={e=>{alterar("signatario","email",e.target.value); if(!dados.canais?.emailDestinatario) alterar("canais","emailDestinatario",e.target.value)}} /></div>
                <div><Label>Telefone</Label><Input value={dados.signatario?.telefone || ""} onChange={e=>alterar("signatario","telefone",e.target.value)} /></div>
              </div>
            </div>

            <div className="contrato-form-section">
              <h3>Plano e condições comerciais</h3>
              <div className="contrato-grid contrato-grid-3">
                <div><Label>Plano contratual</Label><select className="input native" value={dados.comercial?.idPlano || ""} onChange={e=>selecionarPlano(e.target.value)}><option value="">Selecione</option>{dados.planos.map(p=><option key={p.id} value={p.id}>{p.plano}</option>)}</select></div>
                <div><Label>Mensalidade</Label><Input value={dados.comercial?.valorMensal || ""} onChange={e=>alterar("comercial","valorMensal",e.target.value)} /></div>
                <div><Label>Dia de vencimento</Label><Input type="number" min="1" max="31" value={dados.comercial?.diaVencimento || 15} onChange={e=>alterar("comercial","diaVencimento",e.target.value)} /></div>
                <div><Label>Taxa de adesão</Label><Input value={dados.comercial?.valorAdesao || ""} onChange={e=>alterar("comercial","valorAdesao",e.target.value)} /></div>
                <div><Label>Parcelas adesão</Label><Input type="number" min="0" value={dados.comercial?.qtParcelasAdesao || 0} onChange={e=>alterar("comercial","qtParcelasAdesao",e.target.value)} /></div>
                <div><Label>Forma pagamento adesão</Label><Input value={dados.comercial?.formaPagamentoAdesao || "ISENTO"} onChange={e=>alterar("comercial","formaPagamentoAdesao",e.target.value)} /></div>
                <div><Label>Hora técnica</Label><Input value={dados.comercial?.valorHoraTecnica || ""} onChange={e=>alterar("comercial","valorHoraTecnica",e.target.value)} /></div>
                <div><Label>Ativação SAT</Label><Input value={dados.comercial?.valorAtivacaoSat || ""} onChange={e=>alterar("comercial","valorAtivacaoSat",e.target.value)} /></div>
                <div><Label>Qtd. estações adicionais</Label><Input type="number" min="0" value={dados.comercial?.qtEstacoesAdicionais || 0} onChange={e=>alterar("comercial","qtEstacoesAdicionais",e.target.value)} /></div>
                <div><Label>Valor estações adicionais</Label><Input value={dados.comercial?.valorEstacoesAdicionais || ""} onChange={e=>alterar("comercial","valorEstacoesAdicionais",e.target.value)} /></div>
              </div>
              <p className="contrato-hint">Ao confirmar, a mensalidade informada será sincronizada com o ValorCobrado do plano selecionado.</p>
            </div>

            <div className="contrato-form-section">
              <h3>Envio e automação</h3>
              <div className="contrato-switch-row"><div><strong>Atualização anual automática</strong><small>Após 1 ano da assinatura, fabrica nova versão com os dados vigentes.</small></div><SwitchButton checked={dados.atualizacaoAutomatica !== false} onChange={e=>setDados(p=>({...p,atualizacaoAutomatica:!!e.target.checked}))} /></div>
              <div className="contrato-grid contrato-grid-2">
                <div><Label>E-mail destinatário</Label><Input value={dados.canais?.emailDestinatario || dados.signatario?.email || ""} onChange={e=>alterar("canais","emailDestinatario",e.target.value)} /></div>
                <div className="contrato-switch-inline"><SwitchButton checked={dados.canais?.email !== false} onChange={e=>alterar("canais","email",!!e.target.checked)} /><span>Enviar por e-mail</span></div>
                <div><Label>WhatsApp</Label><Input value={dados.canais?.whatsappDestinatario || ""} onChange={e=>alterar("canais","whatsappDestinatario",e.target.value)} /></div>
                <div className="contrato-switch-inline"><SwitchButton checked={!!dados.canais?.whatsapp} onChange={e=>alterar("canais","whatsapp",!!e.target.checked)} /><span>WhatsApp preparado para fase seguinte</span></div>
              </div>
            </div>
          </div>
        )}
        <footer className="contrato-modal-footer"><Button type="button" variant="secondary" onClick={onClose} disabled={enviando}>Cancelar</Button><Button type="button" onClick={enviar} disabled={loading || enviando}>{enviando ? "Gerando contrato..." : "Gerar e enviar"}</Button></footer>
      </section>
    </div>
  );
}

export default function ContratoClientePanel({ codigoCad }) {
  const [loading, setLoading] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [resumo, setResumo] = useState({ atual:null, vigente:null });
  const [modal, setModal] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    if (!codigoCad) return;
    setLoading(true);
    try {
      const [r,l] = await Promise.all([
        api.get(`/cliente/${codigoCad}/contratos/resumo`),
        api.get(`/cliente/${codigoCad}/contratos`),
      ]);
      setResumo(r.data || { atual:null,vigente:null });
      setContratos(Array.isArray(l.data) ? l.data : []);
    } catch (e) { showError(e?.response?.data?.message || "Erro ao carregar contratos."); }
    finally { setLoading(false); }
  }, [codigoCad]);
  useEffect(()=>{ carregar(); },[carregar]);

  const atual = resumo.atual;
  const podeEnviarNovo = !atual || ['ASSINADO','SUBSTITUIDO','CANCELADO','ERRO_GERACAO','REVISAO_NECESSARIA'].includes(atual.STATUS);

  const reenviar = async (c) => {
    try { await api.post(`/cliente/${codigoCad}/contratos/${c.ID_CONTRATO}/reenviar`); showInfo("Reenvio colocado na fila."); carregar(); }
    catch(e){ showError(e?.response?.data?.message || "Erro ao reenviar contrato."); }
  };

  const cancelar = async (c) => {
    if (!window.confirm("Cancelar esta solicitação de assinatura?")) return;
    try { await api.patch(`/cliente/${codigoCad}/contratos/${c.ID_CONTRATO}/cancelar`, { motivo:"Cancelado pelo cadastro do cliente" }); showInfo("Contrato cancelado."); carregar(); }
    catch(e){ showError(e?.response?.data?.message || "Erro ao cancelar contrato."); }
  };

  const abrirDetalhe = async (c) => {
    try { const {data}=await api.get(`/cliente/${codigoCad}/contratos/${c.ID_CONTRATO}`); setDetalhe(data); }
    catch(e){ showError(e?.response?.data?.message || "Erro ao abrir rastreio."); }
  };

  return <>
    <section className="card section contrato-panel">
      <div className="contrato-panel-header">
        <div><div className="section-title">Contrato do cliente</div><p className="section-subtitle">Geração, assinatura eletrônica, rastreamento e atualização anual.</p></div>
        <Button type="button" onClick={()=>setModal(true)} disabled={!codigoCad || !podeEnviarNovo}>{atual && !podeEnviarNovo ? "Contrato pendente" : "Enviar contrato"}</Button>
      </div>
      {loading ? <p>Carregando...</p> : <>
        <div className="contrato-kpis">
          <div><span>Situação atual</span><strong>{statusLabel(atual?.STATUS) || "Sem contrato"}</strong></div>
          <div><span>Contrato vigente</span><strong>{resumo.vigente ? `Versão ${resumo.vigente.NUMERO_VERSAO}` : "Nenhum"}</strong></div>
          <div><span>Próxima atualização</span><strong>{resumo.vigente?.PROXIMA_ATUALIZACAO_EM ? new Date(resumo.vigente.PROXIMA_ATUALIZACAO_EM).toLocaleDateString("pt-BR") : "-"}</strong></div>
        </div>
        {!contratos.length ? <div className="contrato-empty">Nenhum contrato gerado para este cliente.</div> : <div className="contrato-table-wrap"><table className="table-sm contrato-table"><thead><tr><th>Versão</th><th>Origem</th><th>Gerado</th><th>Assinado</th><th>Status</th><th>Ações</th></tr></thead><tbody>{contratos.map(c=><tr key={c.ID_CONTRATO}><td>{c.VERSAO_TEMPLATE} / {c.NUMERO_VERSAO}</td><td>{c.TIPO_GERACAO === "ATUALIZACAO_ANUAL" ? "Automático" : "Manual"}</td><td>{c.GERADO_EM ? new Date(c.GERADO_EM).toLocaleString("pt-BR") : "-"}</td><td>{c.ASSINADO_EM ? new Date(c.ASSINADO_EM).toLocaleString("pt-BR") : "-"}</td><td><span className={`contrato-status ${String(c.STATUS||"").toLowerCase()}`}>{statusLabel(c.STATUS)}</span></td><td><div className="contrato-actions"><Button size="sm" variant="ghost" onClick={()=>abrirDetalhe(c)}>Rastrear</Button>{['AGUARDANDO_ASSINATURA','VISUALIZADO'].includes(c.STATUS) && <><Button size="sm" variant="secondary" onClick={()=>reenviar(c)}>Reenviar</Button><Button size="sm" variant="ghost" onClick={()=>cancelar(c)}>Cancelar</Button></>}</div></td></tr>)}</tbody></table></div>}
      </>}
    </section>
    <ModalPrepararContrato open={modal} codigoCad={codigoCad} onClose={()=>setModal(false)} onSuccess={carregar} />
    {detalhe && <div className="contrato-modal-backdrop" onMouseDown={e=>e.target===e.currentTarget&&setDetalhe(null)}><section className="contrato-modal contrato-modal-small"><header className="contrato-modal-header"><div><small>Rastreamento</small><h2>Contrato {detalhe.contrato?.NUMERO_VERSAO}</h2></div><button onClick={()=>setDetalhe(null)}>×</button></header><div className="contrato-modal-body"><div className="contrato-timeline">{(detalhe.eventos||[]).map(e=><div key={e.ID_EVENTO}><time>{new Date(e.CRIADO_EM).toLocaleString("pt-BR")}</time><strong>{e.TIPO_EVENTO}</strong><p>{e.DESCRICAO}</p></div>)}</div></div></section></div>}
  </>;
}
